import { useState } from 'react';
import {
  bundleNotes,
  downloadText,
  ideasLedger,
  useVault,
  type QueuedNote,
} from '../lib/vault';
import { copyToClipboard } from '../lib/note';
import { Explain } from './Explain';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const KIND_LABEL: Record<QueuedNote['kind'], string> = {
  variant: 'variant',
  summary: 'summary',
  condition: 'condition',
};

/** Transient "✓ done" state for an action button. */
function useFlash(): [string | null, (msg: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  return [
    msg,
    (m: string) => {
      setMsg(m);
      window.setTimeout(() => setMsg(null), 1800);
    },
  ];
}

export function VaultTab() {
  const { notes, ideas, removeNote, clearNotes, addIdea, removeIdea } = useVault();
  const [draft, setDraft] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [flash, setFlash] = useFlash();

  const openNote = notes.find((n) => n.id === preview) ?? null;

  async function copyBundle() {
    if (!notes.length) return;
    const ok = await copyToClipboard(bundleNotes(notes));
    setFlash(ok ? '✓ copied' : 'copy failed');
  }

  function downloadBundle() {
    if (!notes.length) return;
    const ok = downloadText(`i-seng-notes-${today()}.md`, bundleNotes(notes));
    setFlash(ok ? '✓ downloaded' : 'download blocked');
  }

  function downloadLedger() {
    const ok = downloadText(`Genome ideas ledger.md`, ideasLedger(ideas, today()));
    setFlash(ok ? '✓ downloaded' : 'download blocked');
  }

  async function copyLedger() {
    const ok = await copyToClipboard(ideasLedger(ideas, today()));
    setFlash(ok ? '✓ copied' : 'copy failed');
  }

  return (
    <>
      <div className="card">
        <h2>
          Note queue <span className="muted">· {notes.length} staged</span>
          <Explain id="vault" label="How does the vault work?" />
        </h2>

        {notes.length === 0 ? (
          <p className="empty">
            Nothing staged yet. The <strong>＋ note</strong> buttons in Browse,
            Overview and Conditions add notes here as well as copying them to your
            clipboard — stage a few, then export them together.
          </p>
        ) : (
          <>
            <ul className="queue">
              {notes.map((n) => (
                <li key={n.id}>
                  <span className={`cat-chip kind-${n.kind}`}>{KIND_LABEL[n.kind]}</span>
                  <span className="queue-title mono">{n.filename}.md</span>
                  <button
                    className="note-btn"
                    onClick={() => setPreview(preview === n.id ? null : n.id)}
                    aria-expanded={preview === n.id}
                  >
                    {preview === n.id ? 'hide' : 'preview'}
                  </button>
                  <button
                    className="note-btn"
                    onClick={() => downloadText(`${n.filename}.md`, n.body)}
                    title="Save this one note as a .md file"
                  >
                    ↓ save
                  </button>
                  <button
                    className="note-btn danger"
                    onClick={() => removeNote(n.id)}
                    aria-label={`Remove ${n.filename}`}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>

            {openNote && (
              <pre className="note-preview" aria-label={`Preview of ${openNote.filename}`}>
                {openNote.body}
              </pre>
            )}

            <div className="cond-foot">
              <span className="cond-src">
                In memory only — a reload clears the queue, because these embed
                your genotype calls.
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="note-btn" onClick={copyBundle}>
                  {flash ?? 'copy all'}
                </button>
                <button className="note-btn" onClick={downloadBundle}>
                  ↓ download all
                </button>
                <button className="note-btn danger" onClick={clearNotes}>
                  clear
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>
          Ideas ledger <span className="muted">· {ideas.length}</span>
        </h2>
        <p className="cond-summary" style={{ marginTop: 0 }}>
          Questions and hunches to follow up. Unlike the note queue, this one{' '}
          <strong>does survive a reload</strong> — it is your own writing, stored
          in this browser only. Don’t paste genotype calls in here if you would
          rather nothing persisted.
        </p>

        <form
          className="controls"
          onSubmit={(e) => {
            e.preventDefault();
            addIdea(draft);
            setDraft('');
          }}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. why is my Ti/Tv so low? check whether the array explains it"
            aria-label="New idea"
          />
          <button className="primary" type="submit" disabled={!draft.trim()}>
            Add
          </button>
        </form>

        {ideas.length > 0 && (
          <>
            <ul className="ideas">
              {ideas.map((i) => (
                <li key={i.id}>
                  <span className="idea-date mono">{i.created}</span>
                  <span className="idea-text">{i.text}</span>
                  <button
                    className="note-btn danger"
                    onClick={() => removeIdea(i.id)}
                    aria-label="Remove idea"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <div className="cond-foot">
              <span className="cond-src">Exports as a single dated markdown note.</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="note-btn" onClick={copyLedger}>copy</button>
                <button className="note-btn" onClick={downloadLedger}>↓ download</button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Getting it into Obsidian</h2>
        <p className="cond-summary" style={{ marginTop: 0 }}>
          The vault lives at <code>./brain/</code>, is git-ignored, and never
          syncs. Create it once, then point Obsidian at it.
        </p>
        <pre className="note-preview">{`npm run brain:init          # create ./brain/ from vault-template/
# Obsidian → Open folder as vault → choose brain/

# or write a note straight from the CLI:
npm run note -- --rsid rs1815739 --chrom 11 --pos 66560624 \\
  --genotype CT --note "ACTN3 — follow up on the endurance literature."`}</pre>
        <div className="note" style={{ marginTop: 14 }}>
          <strong>Reach it remotely over Tailscale, not cloud sync.</strong> iCloud,
          Google Drive and Synology Drive all place copies on servers outside your
          control. If notes might ever land in a synced vault, use the CLI’s{' '}
          <code>--redact</code> flag so raw genotype calls are never written.
        </div>
      </div>
    </>
  );
}
